import React, { FC, useCallback, useMemo, useState } from 'react';

import {
	BUTTON_TYPES,
	EvBodyMedium,
	EvButton,
	EvFlexColumn,
	EvFlexRow,
	EvHeadingSmall,
	TEXT_COLOR,
	TITLE_MODES,
	TOAST_STATUS
} from '@evinced-private/ui-common';

import { EvDivider } from 'src/common/components/ev-divider';
import { PlatformPopup } from 'src/common/components/platform-popup/PlatformPopup';
import { useNotifications } from 'src/common/providers/notificationsProvider/NotificationsConsumer';
import { Logger } from 'src/common/services/Logger';

import { Label, LabelType, LabelValue, PanelMode } from '../../types';
import { DiscardLabelChangesModal } from '../discard-label-changes-modal';

import { LabelDetailsSection } from './sections/label-details';
import { LABEL_DETAILS_LABELS } from './sections/label-details/LabelDetailsSection.labels';
import { LabelValuesSection } from './sections/label-values';
import { LABEL_VALUES_LABELS } from './sections/label-values/LabelValuesSection.labels';
import { LABEL_PANEL_WIDTH } from './LabelPanel.constants';
import { LABEL_PANEL_LABELS } from './LabelPanel.labels';
import { LabelPanelProps } from './LabelPanel.types';
import {
	cloneLabel,
	processLabelValuesCsvUpload,
	sortLabelValuesForDisplay,
	validateDescription,
	validateDisplayName,
	validateKey,
	validateNewValue
} from './LabelPanel.utils';

export const LabelPanel: FC<LabelPanelProps> = ({
	activeLabel,
	panelMode,
	productId,
	isSaving,
	existingKeys,
	onSave,
	onClose,
	onDelete
}) => {
	const { toast } = useNotifications();
	const [editedLabel, setEditedLabel] = useState<Label>(() => cloneLabel(activeLabel));
	const [hasBeenTouched, setHasBeenTouched] = useState(false);
	const [displayNameError, setDisplayNameError] = useState<string | undefined>();
	const [descriptionError, setDescriptionError] = useState<string | undefined>();
	const [keyError, setKeyError] = useState<string | undefined>();
	const [csvUploadError, setCsvUploadError] = useState<string | undefined>();
	const [csvRejectedFileValues, setCsvRejectedFileValues] = useState<string[] | undefined>();
	const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

	const markTouched = useCallback(() => {
		setHasBeenTouched(true);
	}, []);

	const sortedValuesForTable = useMemo(
		() => sortLabelValuesForDisplay(editedLabel.values),
		[editedLabel.values]
	);

	const isCoreLabel = editedLabel.type === LabelType.CORE;
	const isAddMode = panelMode === PanelMode.ADD;
	const isCustomEditMode = !isAddMode && !isCoreLabel;

	const isValid = !displayNameError && !descriptionError && !keyError;
	const hasRequiredFields =
		!!editedLabel.displayName.trim() && (!isAddMode || !!editedLabel.key.trim());

	const canSave = useMemo(() => isValid && hasRequiredFields, [isValid, hasRequiredFields]);

	const handleKeyChange = useCallback(
		(value: string) => {
			markTouched();
			setEditedLabel((prev) => ({ ...prev, key: value }));
			setKeyError(validateKey(value, existingKeys));
		},
		[existingKeys, markTouched]
	);

	const handleDisplayNameChange = useCallback(
		(value: string) => {
			markTouched();
			setEditedLabel((prev) => ({ ...prev, displayName: value }));
			setDisplayNameError(validateDisplayName(value));
		},
		[markTouched]
	);

	const handleDescriptionChange = useCallback(
		(value: string) => {
			markTouched();
			setEditedLabel((prev) => ({ ...prev, description: value }));
			setDescriptionError(validateDescription(value));
		},
		[markTouched]
	);

	const handleAddValue = useCallback(
		(value: string) => {
			markTouched();
			const error = validateNewValue(value, editedLabel.values);
			if (error) {
				return error;
			}
			const newValue: LabelValue = {
				value: value.trim(),
				dashboardVisible: true,
				isSessionNew: true
			};
			setCsvUploadError(undefined);
			setCsvRejectedFileValues(undefined);
			setEditedLabel((prev) => ({ ...prev, values: [...prev.values, newValue] }));
			return undefined;
		},
		[editedLabel?.values, markTouched]
	);

	const handleUploadCsvContent = useCallback(
		(text: string) => {
			markTouched();
			setCsvUploadError(undefined);
			setCsvRejectedFileValues(undefined);

			let uploadResult: ReturnType<typeof processLabelValuesCsvUpload> | undefined;

			setEditedLabel((prev) => {
				uploadResult = processLabelValuesCsvUpload(text, prev.values);
				if (uploadResult.emptyFile || uploadResult.invalid.length > 0) {
					return prev;
				}
				return { ...prev, values: uploadResult.nextValues };
			});

			if (!uploadResult) {
				return;
			}
			if (uploadResult.emptyFile) {
				setCsvUploadError(LABEL_VALUES_LABELS.UPLOAD_EMPTY_FILE);
				return;
			}
			if (uploadResult.invalid.length > 0) {
				setCsvRejectedFileValues(uploadResult.rejectedFileValues ?? uploadResult.invalid);
				return;
			}

			const added = uploadResult.addedCount ?? 0;
			const skipped = uploadResult.skippedDuplicateCount ?? 0;
			if (added > 0) {
				toast?.({
					show: true,
					status: TOAST_STATUS.SUCCESS,
					announcement: LABEL_VALUES_LABELS.CSV_UPLOAD_TOAST_ADDED(added, skipped)
				});
			} else if (skipped > 0) {
				toast?.({
					show: true,
					status: TOAST_STATUS.SUCCESS,
					announcement: LABEL_VALUES_LABELS.CSV_UPLOAD_TOAST_ALL_DUPLICATES(skipped)
				});
			}
		},
		[toast, markTouched]
	);

	const handleRemoveValue = useCallback(
		(value: string) => {
			markTouched();
			setEditedLabel((prev) => ({
				...prev,
				values: prev.values.filter((v) => v.value !== value)
			}));
		},
		[markTouched]
	);

	const handleToggleDashboardVisibility = useCallback(
		(value: string) => {
			markTouched();
			setEditedLabel((prev) => ({
				...prev,
				values: prev.values.map((v) =>
					v.value === value ? { ...v, dashboardVisible: !(v.dashboardVisible ?? true) } : v
				)
			}));
		},
		[markTouched]
	);

	const handleSave = useCallback(async () => {
		try {
			await onSave(productId, editedLabel);
			toast?.({
				show: true,
				status: TOAST_STATUS.SUCCESS,
				announcement: LABEL_PANEL_LABELS.SAVE_SUCCESS
			});
		} catch (error) {
			Logger.error('LabelPanel -> handleSave failed:', error);
			toast?.({
				show: true,
				status: TOAST_STATUS.FAIL,
				announcement: LABEL_PANEL_LABELS.SAVE_ERROR
			});
		}
	}, [productId, onSave, toast, editedLabel]);

	const requestClose = useCallback(() => {
		if (hasBeenTouched) {
			setShowDiscardConfirm(true);
			return;
		}
		onClose();
	}, [hasBeenTouched, onClose]);

	const handleDiscardChanges = useCallback(() => {
		setShowDiscardConfirm(false);
		onClose();
	}, [onClose]);

	const handleKeepEditing = useCallback(() => {
		setShowDiscardConfirm(false);
	}, []);

	const { displayName, description } = editedLabel;

	const title = useMemo(
		() =>
			panelMode === PanelMode.EDIT
				? LABEL_PANEL_LABELS.TITLE_EDIT(displayName, isCoreLabel)
				: LABEL_PANEL_LABELS.TITLE_ADD,
		[panelMode, displayName, isCoreLabel]
	);

	return (
		<>
			<PlatformPopup
				popupWidth={LABEL_PANEL_WIDTH}
				isControlled
				isBgTransparent
				fullHeight
				horizontalPosition="right"
				titleMode={TITLE_MODES.NORMAL}
				title={title}
				isOpen
				onClose={requestClose}
				isPopupLoading={isSaving}
				buttons={[
					{
						onClick: requestClose,
						title: LABEL_PANEL_LABELS.CANCEL,
						type: BUTTON_TYPES.SECONDARY
					},
					{
						onClick: handleSave,
						title: LABEL_PANEL_LABELS.SAVE,
						type: BUTTON_TYPES.PRIMARY,
						disabled: !canSave || isSaving
					}
				]}
			>
				{!isAddMode && (
					<EvBodyMedium color={TEXT_COLOR.CONTENT_3}>
						{LABEL_PANEL_LABELS.SUBTITLE_EDIT}
					</EvBodyMedium>
				)}
				<EvFlexRow padding={['NONE', 'NONE', 'MEDIUM', 'NONE']}>
					<EvDivider />
				</EvFlexRow>
				<EvFlexColumn gap="XLARGE">
					<EvFlexColumn gap="LARGE">
						<EvHeadingSmall as="h3" color={TEXT_COLOR.CONTENT_2}>
							{LABEL_DETAILS_LABELS.SECTION_TITLE}
						</EvHeadingSmall>
						<LabelDetailsSection
							displayName={displayName}
							description={description ?? ''}
							labelKey={editedLabel.key}
							isKeyEditable={isAddMode}
							keyError={keyError}
							onKeyChange={handleKeyChange}
							onDisplayNameChange={handleDisplayNameChange}
							onDescriptionChange={handleDescriptionChange}
							displayNameError={displayNameError}
							descriptionError={descriptionError}
						/>
					</EvFlexColumn>
					<EvFlexRow>
						<EvDivider />
					</EvFlexRow>
					<EvFlexColumn gap="LARGE">
						<EvHeadingSmall as="h3" color={TEXT_COLOR.CONTENT_2}>
							{LABEL_VALUES_LABELS.SECTION_TITLE}
						</EvHeadingSmall>
						<LabelValuesSection
							values={sortedValuesForTable}
							showDashboardVisibility={!isAddMode && isCoreLabel}
							onAddValue={handleAddValue}
							onRemoveValue={handleRemoveValue}
							onToggleDashboardVisibility={handleToggleDashboardVisibility}
							onUploadCsvContent={handleUploadCsvContent}
							onFormTouched={markTouched}
							csvUploadError={csvUploadError}
							csvRejectedFileValues={csvRejectedFileValues}
						/>
					</EvFlexColumn>
					{isCustomEditMode && (
						<>
							<EvFlexRow>
								<EvDivider />
							</EvFlexRow>
							<EvButton
								type={BUTTON_TYPES.SECONDARY_WITH_TRANSPARENT_BG}
								onClick={onDelete}
								aria-label={LABEL_PANEL_LABELS.DELETE_LABEL_ARIA(displayName)}
							>
								{LABEL_PANEL_LABELS.DELETE_LABEL}
							</EvButton>
						</>
					)}
				</EvFlexColumn>
			</PlatformPopup>
			{showDiscardConfirm && (
				<DiscardLabelChangesModal
					onDiscard={handleDiscardChanges}
					onKeepEditing={handleKeepEditing}
				/>
			)}
		</>
	);
};
